// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerMenuStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, computed, action } from 'mobx';
import constants from './data/Izenda.ReportDesignerConstants';
import { filter, find, forEach, isNil, isArray, isEmpty, includes, map } from 'lodash/fp';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import localizer from 'share/localization/Izenda.Localizer';
import Constants from './data/Izenda.ReportDesignerConstants';

/**
 * Menu Item Class
 */
class MenuItem {
  @observable visible = true;

  constructor(detail: any) {
    detail = detail || {};
    this.key = detail.key;
    this.icon = detail.icon;
    this.title = detail.title;
    this.permissions = detail.permissions;
    this.licenseKey = detail.licenseKey;
    this.tooltip = detail.tooltip;
    this.hoverShowTooltip = detail.hoverShowTooltip;
  }
}

/**
 * Report Designer Menu Store
 */
class ReportDesignerMenuStore {
  @observable enableMenuItems = [];
  @observable contextMenuActiveLink;
  @observable menuStatus = false;

  constructor(reportId: string) {
    this.contextMenu = constants.MENU_DEFINITION;
    this.populateMenuItems();
    this.reportId = reportId;
  }

  @computed
  get visibleMenuItems() {
    return filter('visible', this.enableMenuItems);
  }

  /**
   * Change active link
   * @param {String} newLink
   */
  @action
  changeActiveLink(newLink: String): void {
    if (this.contextMenuActiveLink !== newLink) {
      this.contextMenuActiveLink = newLink;
    }
  }

  /**
   * Update menu state
   * @param {Boolean} newState
   */
  updateMenuState(newState: Boolean): void {
    this.menuStatus = newState;
  }

  /**
   * Remove menu item out off current enable menu items.
   * @param  {String} key
   */
  @action
  excludeMenuItemByKey(key: String): void {
    const matchingItem = find(item => item.key === key, this.enableMenuItems);
    this.enableMenuItems.remove(matchingItem);
  }

  /**
   * Toggle item visible or invisible
   * @param {String}  key
   * @param {Boolean} value
   */
  @action
  toggleInvisibleItem(key: String, value: Boolean): void {
    const matchingItem = find(item => item.key === key, this.enableMenuItems);

    matchingItem && (matchingItem.visible = value);
  }

  /**
   * Build menu items from config data.
   */
  @action
  populateMenuItems(): void {
    this.enableMenuItems.clear();
    forEach(item => {
      this.enableMenuItems.push(new MenuItem(item));
    }, this.contextMenu);
  }

  /**
   * Rebuild menu based on privilege and license.
   * @param {Object} authorization
   */
  @action
  filterContextMenuByLicense(authorization: any): void {
    this.populateMenuItems();
    forEach(item => {
      if (
        !(isNil(item.permissions) || authorization.checkPrivileges(item.permissions)) ||
        !(isNil(item.licenseKey) || authorization.checkLicense(item.licenseKey))
      ) {
        this.excludeMenuItemByKey(item.key);
      }
    }, this.contextMenu);
  }

  /**
   * Filter Context Menu For Mobile
   */
  @action
  filterContextMenuForMobile(): void {
    forEach(item => {
      if (item.excludeForMobile === true) {
        this.excludeMenuItemByKey(item.key);
      }
    }, this.contextMenu);
  }

  /**
   * Update menu status.
   * @param {Boolean} menuStatus
   */
  @action
  updateMenuStatus(menuStatus: Boolean): void {
    const noMenuItem = 0;
    if (this.enableMenuItems.length > noMenuItem) {
      this.validateReportIdNoCache(this.reportId).then(validationStatus => {
        this.contextMenuActiveLink = constants.MENU_ACTIVE_LINK;
        if (validationStatus === constants.FILTER_CHANGE_STATE.ADDED || validationStatus === constants.FILTER_CHANGE_STATE.UPDATED) {
          this.contextMenuActiveLink = constants.MENU_DATASOURCE_LINK;
        }
      });
      this.menuStatus = isNil(menuStatus) ? true : menuStatus;
    }
  }

  /**
   * Get report update status to set menu active link
   * @param {string} reportId
   */
  @action
  validateReportIdNoCache(reportId: string): any {
    if (isNil(reportId)) return Promise.resolve(true);
    return fetcher.validateReportIdNoCache(reportId, false).then(result => {
      if (!result.success) {
        if (reportId) {
          const messagesResult = result.messages;
          const msg =
            !isNil(messagesResult) &&
            isArray(messagesResult) &&
            messagesResult.length > 0 &&
            !isEmpty(messagesResult[0].key)
              ? messagesResult[0].key
              : localizer.getText('REPORTS_CONSTANTS_REPORT_MESSAGE_INVALID_REPORT');
          return msg;
        }
      }
      return result.message;
    });
  }
}

export default ReportDesignerMenuStore;



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerMenuStore.js